import { ResponsiveLine } from "@nivo/line";
import { useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { tokens } from "../../../../theme";
import ConnString from "../../../../ConnString.js";

const Connstring2 = ConnString.connstring;

const MONTH_ORDER = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTH_ABBR = { January:"Jan", February:"Feb", March:"Mar", April:"Apr", May:"May", June:"Jun", July:"Jul", August:"Aug", September:"Sep", October:"Oct", November:"Nov", December:"Dec" };

const RetainerChart = ({
  isCustomLineColors = false,
  isDashboard = false,
  dateFrom,         // e.g. "2026-01-01"
  dateTo,           // e.g. "2026-12-31"
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);        // raw api rows
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const compID = sessionStorage.getItem("compID");

  useEffect(() => {
    let alive = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        // ✅ adjust endpoint/method to your backend
        const res = await fetch(`${Connstring2}/api/RetainerChart`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            companyID: compID,
            datefrom: dateFrom,
            dateto: dateTo,
          }),
        });

        if (!res.ok) throw new Error(`API error: ${res.status}`);

        const data = await res.json();
        if (!alive) return;

        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || "Failed to load chart data.");
        setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchData();

    return () => {
      alive = false;
    };
  }, [Connstring2, compID, dateFrom, dateTo]);

  // ✅ transform API rows -> Nivo series
  const lineData = useMemo(() => {
    // group: Statusx -> Map(Monthx -> AmountY)
    const byStatus = new Map();


    for (const r of rows) {
      const status = r?.Statusx ?? "Unknown";
      const month = r?.Monthx ?? "";
      const y = Number(r?.AmountY ?? 0);

      if (!byStatus.has(status)) byStatus.set(status, new Map());
      byStatus.get(status).set(month, y);

      
    }

    // ensure consistent month order on x-axis
    const allMonths = MONTH_ORDER.filter(m => {
      // keep only months present at least once (optional)
      return rows.some(r => r?.Monthx === m);
    });

    const monthsToUse = allMonths.length ? allMonths : MONTH_ORDER;

    return Array.from(byStatus.entries()).map(([status, monthMap]) => ({
      id: status,
      data: monthsToUse.map((m) => ({
        x: MONTH_ABBR[m] || m,                  // show Jan/Feb/... (or use full month)
        y: Number(monthMap.get(m) ?? 0),        // fill missing months with 0
      })),
    }));
  }, [rows]);

    console.log("API rows:", rows);
    console.log("Nivo lineData:", lineData);

  if (loading) return <div style={{ padding: 8 }}>Loading chart…</div>;
  if (error) return <div style={{ padding: 8, color: "tomato" }}>{error}</div>;

  return (
    <ResponsiveLine
      data={lineData}
      theme={{
        axis: {
          domain: { line: { stroke: colors.grey[100] } },
          legend: { text: { fill: colors.grey[100] } },
          ticks: {
            line: { stroke: colors.grey[100], strokeWidth: 1 },
            text: { fill: colors.grey[100] },
          },
        },
        legends: { text: { fill: colors.grey[100] } },
        tooltip: { container: { color: colors.primary[500] } },
      }}
     // colors={isDashboard ? { datum: "color" } : { scheme: "nivo" }}
      colors={{ scheme: "nivo" }}
      margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
      xScale={{ type: "point" }}
      yScale={{ type: "linear", min: "auto", max: "auto", stacked: false, reverse: false }}
      curve="catmullRom"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        orient: "bottom",
        tickSize: 0,
        tickPadding: 5,
        tickRotation: 0,
        legend: isDashboard ? undefined : "Month",
        legendOffset: 36,
        legendPosition: "middle",
      }}
      axisLeft={{
        orient: "left",
        tickValues: 5,
        tickSize: 3,
        tickPadding: 5,
        tickRotation: 0,
        legend: isDashboard ? undefined : "Amount",
        legendOffset: -50,
        legendPosition: "middle",
      }}
      enableGridX={false}
      enableGridY={false}
      pointSize={8}
      pointColor={{ theme: "background" }}
      pointBorderWidth={2}
      pointBorderColor={{ from: "serieColor" }}
      pointLabelYOffset={-12}
      useMesh={true}
      legends={[
        {
          anchor: "bottom-right",
          direction: "column",
          translateX: 100,
          itemWidth: 100,
          itemHeight: 20,
          symbolSize: 12,
          symbolShape: "circle",
        },
      ]}
    />
  );
};

export default RetainerChart;
